import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return null;
  const camelCaseObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    camelCaseObj[camelKey] = obj?.[key];
  }
  return camelCaseObj;
};

// Helper function to convert array of objects from snake_case to camelCase
const toCamelCaseArray = (arr) => {
  if (!arr) return [];
  return arr?.map(obj => toCamelCase(obj));
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return null;
  const snakeCaseObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    snakeCaseObj[snakeKey] = obj?.[key];
  }
  return snakeCaseObj;
};

export const notificationService = {
  /**
   * Get all active notifications for users
   * @returns {Promise<Array>}
   */
  async getActiveNotifications() {
    try {
      // First deactivate expired notifications
      await supabase?.rpc('deactivate_expired_notifications');

      const { data, error } = await supabase
        ?.from('admin_notifications')
        ?.select(`
          *,
          created_by_profile:user_profiles!created_by(full_name, email)
        `)
        ?.eq('is_active', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching active notifications:', error);
      throw error;
    }
  },

  /**
   * Get all notifications (admin only)
   * @returns {Promise<Array>}
   */
  async getAllNotifications() {
    try {
      const { data, error } = await supabase
        ?.from('admin_notifications')
        ?.select(`
          *,
          created_by_profile:user_profiles!created_by(full_name, email)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching all notifications:', error);
      throw error;
    }
  },

  /**
   * Create a new notification (admin only)
   * @param {Object} notification - { title, message, type, expiresAt }
   * @returns {Promise<Object>}
   */
  async createNotification(notification) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const notificationData = {
        title: notification?.title,
        message: notification?.message,
        type: notification?.type || 'info',
        created_by: user?.id,
        is_active: true
      };

      if (notification?.expiresAt) {
        notificationData.expires_at = notification?.expiresAt;
      }

      const { data, error } = await supabase
        ?.from('admin_notifications')
        ?.insert(toSnakeCase(notificationData))
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  /**
   * Update a notification (admin only)
   * @param {string} id - Notification ID
   * @param {Object} updates - { title, message, type, isActive, expiresAt }
   * @returns {Promise<Object>}
   */
  async updateNotification(id, updates) {
    try {
      const updateData = {};
      
      if (updates?.title !== undefined) updateData.title = updates?.title;
      if (updates?.message !== undefined) updateData.message = updates?.message;
      if (updates?.type !== undefined) updateData.type = updates?.type;
      if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;
      if (updates?.expiresAt !== undefined) updateData.expires_at = updates?.expiresAt;

      const { data, error } = await supabase
        ?.from('admin_notifications')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating notification:', error);
      throw error;
    }
  },

  /**
   * Delete a notification (admin only)
   * @param {string} id - Notification ID
   * @returns {Promise<void>}
   */
  async deleteNotification(id) {
    try {
      const { error } = await supabase
        ?.from('admin_notifications')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  /**
   * Deactivate a notification (admin only)
   * @param {string} id - Notification ID
   * @returns {Promise<Object>}
   */
  async deactivateNotification(id) {
    try {
      return await this.updateNotification(id, { isActive: false });
    } catch (error) {
      console.error('Error deactivating notification:', error);
      throw error;
    }
  },

  /**
   * Subscribe to notification changes (realtime)
   * @param {Function} callback - Callback function for changes
   * @returns {Object} - Subscription object
   */
  subscribeToNotifications(callback) {
    try {
      const channel = supabase
        ?.channel('admin_notifications_changes')
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'admin_notifications'
          },
          (payload) => {
            callback(payload);
          }
        )
        ?.subscribe();

      return channel;
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      throw error;
    }
  }
};